'use client'

import { useMetrica } from 'next-yandex-metrica'
import Image from 'next/image'
import Link from 'next/link'
import { useState, useEffect } from 'react'
import * as Tooltip from '@radix-ui/react-tooltip'

// Social media links with real URLs and improved descriptions
const socialLinks = [
  {
    id: 'youtube',
    name: 'YouTube',
    url: 'https://www.youtube.com/@nuvertal?sub_confirmation=1',
    icon: (
      <Image
        src="/assets/img/youtube.svg"
        alt="Логотип YouTube"
        width={48}
        height={48}
        priority
        className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12"
      />
    ),
    tooltip: 'Записи эфиров и подкасты',
  },
  {
    id: 'rutube',
    name: 'RuTube',
    url: 'https://rutube.ru/channel/35723907/',
    icon: (
      <Image
        src="/assets/img/rutube.svg"
        alt="Логотип RuTube"
        width={48}
        height={48}
        priority
        className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12"
      />
    ),
    tooltip: 'Альтернативная видеоплатформа',
  },
  {
    id: 'twitch',
    name: 'Twitch',
    url: 'https://www.twitch.tv/nuvertal_dnb',
    icon: (
      <Image
        src="/assets/img/twitch.svg"
        alt="Логотип Twitch"
        width={48}
        height={48}
        priority
        className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12"
      />
    ),
    tooltip: 'Прямые трансляции и общение в чате',
  },
  {
    id: 'vk',
    name: 'VK',
    url: 'https://vk.com/nuvertal_music',
    icon: (
      <Image
        src="/assets/img/vk.svg"
        alt="Логотип VK"
        width={48}
        height={48}
        priority
        className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12"
      />
    ),
    tooltip: 'Новости и анонсы выпусков',
  },
  {
    id: 'telegram',
    name: 'Telegram',
    url: 'https://t.me/nuvertal_music',
    icon: (
      <Image
        src="/assets/img/telegram-logo-circled.svg"
        alt="Логотип Telegram"
        width={40}
        height={40}
        priority
        className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12"
      />
    ),
    tooltip: 'Оперативные обновления и общение',
  },
]

export default function HeroSection() {
  const { reachGoal } = useMetrica()
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 })

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      // Calculate mouse position as percentage of window size
      const x = e.clientX / window.innerWidth
      const y = e.clientY / window.innerHeight

      setMousePosition({ x, y })
    }

    window.addEventListener('mousemove', handleMouseMove)

    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
    }
  }, [])

  // Calculate parallax offset (subtle movement)
  const offsetX = (mousePosition.x - 0.5) * -20 // -10px to 10px movement
  const offsetY = (mousePosition.y - 0.5) * -10 // -5px to 5px movement

  return (
    <Tooltip.Provider delayDuration={300}>
      <section className="relative min-h-[500px] flex items-center py-8 md:py-0">
        {/* Background Image */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          <div
            className="absolute inset-0 transition-transform duration-200 ease-out"
            style={{
              transform: `translate(${offsetX}px, ${offsetY}px) scale(1.05)`,
            }}
          >
            <Image
              src="/assets/img/hero-bg.jpeg"
              alt="Фон шапки для Радио Саня"
              width={1900}
              height={750}
              sizes="100vw"
              className="object-cover w-full h-full"
              priority
              fetchPriority="high"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent"></div>
          </div>
        </div>

        {/* Content */}
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-display text-radio-blue mb-6">
              Радио Саня
            </h1>
            <p className="text-lg md:text-xl text-gray-200 leading-relaxed mb-8">
              это ламповое drum and bass шоу, где каждая трансляция — это маленький домашний рейв, в
              который хочется возвращаться. Проект родился из любви к ломаным ритмам, культуре
              рейвов 2000-х и желания делиться позитивом.
            </p>
            <p className="text-lg md:text-xl text-gray-200 leading-relaxed mb-8">
              На волнах шоу - только Drum and Bass, тёплая атмосфера и заряд позитива
            </p>

            {/* Social Media Icons */}
            <div className="flex flex-wrap gap-3 sm:gap-4 md:gap-6 mt-6">
              {socialLinks.map((social, index) => (
                <Tooltip.Root key={index}>
                  <Tooltip.Trigger asChild>
                    <div className="relative">
                      <Link
                        href={social.url}
                        target="_blank"
                        onClick={() => reachGoal(`hero-link-${social.id}-click`)}
                        rel="noopener noreferrer"
                        className="flex items-center justify-center transition-transform hover:scale-110"
                        aria-label={`${social.name} - ${social.tooltip}`}
                      >
                        {social.icon}
                      </Link>
                    </div>
                  </Tooltip.Trigger>
                  <Tooltip.Portal>
                    <Tooltip.Content
                      className="px-3 py-1.5 bg-black/80 text-white text-sm rounded-md max-w-[250px] z-50"
                      sideOffset={5}
                      collisionPadding={10}
                      side="bottom"
                    >
                      <span className="font-bold">{social.name}:</span> {social.tooltip}
                      <Tooltip.Arrow className="fill-black/80" width={10} height={5} />
                    </Tooltip.Content>
                  </Tooltip.Portal>
                </Tooltip.Root>
              ))}
            </div>
          </div>
        </div>
      </section>
    </Tooltip.Provider>
  )
}
